import {useAuth as useItsaiAuth} from "@it-s-ai/auth";
import {useStores} from "stores/rootStore";
import {withLanguagePrefix} from "i18n/hooks";
import {persistAuthData} from "persistence";
import {restoreLoginState} from "loginDrafts";
import {logEvent} from "analytics";
import {gtmEvent} from 'utils/gtm';
import {isExtensionRoute, paramsGet, reprNetworkError} from "utils";
import {consumePostLoginAction} from "postLoginAction";
import {consumePendingGuestSession} from "guestCheckout";
import {useLanguage} from "i18n/LanguageProvider";
import {useNavigate} from "react-router-dom";
import {isFeatureEnabled, FEATURES} from "config";

export const LoginCompleteHandler = () => {
    const root = useStores();
    const {user: userStore} = root;
    const navigate = useNavigate();
    const {lang} = useLanguage();

    useItsaiAuth({
        onLoginComplete: async ({token, uuid}) => {
            try {
                await userStore.auth(token);

                persistAuthData(userStore);

                // Pay-first guest checkout claimed: the backend moved the Stripe
                // purchase onto this account during signup/login. Confirm + done —
                // skip draft-scan replay and the (old) login→checkout handoff.
                const guestSession = consumePendingGuestSession();
                if (guestSession) {
                    await root.subscription.confirmActivated({soldTrial: guestSession.trial});
                    navigate(withLanguagePrefix(lang, "/main/subscription"));
                    return;
                }

                // Text or files staged before the login redirect: always re-hydrate them so the
                // user's state survives the login; the scan re-runs only when the login was started
                // from the limit auth form (see persistLoginState).
                const pendingAction = consumePostLoginAction();
                restoreLoginState(root, pendingAction);

                if (pendingAction?.type === 'free_premium_enroll' && isFeatureEnabled(FEATURES.FREE_PREMIUM)) {
                    root.user.enroll().then(enrolled => {
                        if (enrolled) root.modal.showModal("questionnaire-thankyou");
                    });
                } else {
                    if (pendingAction?.type === 'upgrade') root.input.openPlanCheckout(pendingAction.plan);
                    const redirect = paramsGet('redirect');
                    if (redirect) {
                        navigate(redirect);
                    } else if (window.location.pathname.includes('/auth/callback')) {
                        // Replacing, not pushing: the callback URL carries a one-shot code, and
                        // Back onto it would run the exchange again and land on its error.
                        const back = pendingAction?.type === 'account_page' ? pendingAction.path : withLanguagePrefix(lang, "/");
                        navigate(back, {replace: true});
                    }
                }
            } catch (e) {
                await logEvent(gtmEvent('GTM_user_login_error', {
                    error: reprNetworkError(e).message, surface: isExtensionRoute() ? 'extension' : 'app', page_type: 'app_page', entrypoint: 'login_complete',
                }));
            }
        },
    });

    return null;
};
